import { useEffect, useState } from 'react';
import { LuPin, LuX } from 'react-icons/lu';
import { Checkbox } from 'antd';
import { motion } from 'motion/react';
import { ml } from 'src/_shared/services/utils.js';
import { lightenHex, accentRgba, darkenHex } from './utils.js';

function SettingsModal({
	headerTitle: initialHeaderTitle,
	headerSubtext: initialHeaderSubtext,
	accentColor: initialAccentColor,
	pinnedColor: initialPinnedColor,
	gradientStart: initialGradientStart,
	gradientMid: initialGradientMid,
	gradientEnd: initialGradientEnd,
	showAuthor: initialShowAuthor,
	showViews: initialShowViews,
	showCount: initialShowCount,
	defaultAuthor: initialDefaultAuthor,
	onSave,
	onClose,
	currentUser,
	allMultiLingualData,
}) {
	// All edits are local until Save is clicked
	const [headerTitle, setHeaderTitle] = useState(initialHeaderTitle);
	const [headerSubtext, setHeaderSubtext] = useState(initialHeaderSubtext);
	const [accentColor, setAccentColor] = useState(initialAccentColor);
	const [pinnedColor, setPinnedColor] = useState(initialPinnedColor);
	const [gradientStart, setGradientStart] = useState(initialGradientStart);
	const [gradientMid, setGradientMid] = useState(initialGradientMid);
	const [gradientEnd, setGradientEnd] = useState(initialGradientEnd);
	const [showAuthor, setShowAuthor] = useState(initialShowAuthor);
	const [showViews, setShowViews] = useState(initialShowViews);
	const [showCount, setShowCount] = useState(initialShowCount);
	const [defaultAuthor, setDefaultAuthor] = useState(initialDefaultAuthor);
	const [accentDraft, setAccentDraft] = useState(initialAccentColor);
	const [pinnedDraft, setPinnedDraft] = useState(initialPinnedColor);

	useEffect(() => {
		document.body.style.overflow = 'hidden';
		const handleEsc = (e) => {
			if (e.key === 'Escape') onClose();
		};

		document.addEventListener('keydown', handleEsc);
		return () => {
			document.body.style.overflow = '';
			document.removeEventListener('keydown', handleEsc);
		};
	}, [onClose]);

	const presetColors = [
		'#0d9488',
		'#3b82f6',
		'#8b5cf6',
		'#ec4899',
		'#f59e0b',
		'#ef4444',
		'#10b981',
		'#6366f1',
	];
	const gradientPreview = `linear-gradient(to right, ${gradientStart}, ${gradientMid}, ${gradientEnd})`;

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-4"
			onClick={onClose}
		>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{ duration: 0.3 }}
				className="fixed inset-0"
				style={{
					background: 'rgba(15,23,42,0.5)',
					backdropFilter: 'blur(4px)',
				}}
			/>

			<motion.div
				initial={{ opacity: 0, scale: 0.95 }}
				animate={{ opacity: 1, scale: 1 }}
				exit={{ opacity: 0, scale: 0.95 }}
				transition={{ duration: 0.3, ease: 'easeOut' }}
				className="relative flex w-full flex-col overflow-hidden rounded-2xl bg-white"
				style={{
					maxWidth: 520,
					maxHeight: '85vh',
					boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
				}}
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header */}
				<div
					className="flex items-center justify-between p-6"
					style={{ paddingBottom: 0 }}
				>
					<h2
						style={{
							color: '#1e293b',
							fontSize: 18,
							fontWeight: 600,
							margin: 0,
						}}
					>
						{ml('Announcements Settings', currentUser, allMultiLingualData)}
					</h2>
					<button
						type="button"
						onClick={onClose}
						style={{
							width: 32,
							height: 32,
							borderRadius: 12,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: 0,
							lineHeight: 0,
							color: '#475569',
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							transition: 'all 0.2s',
						}}
						onMouseOver={(e) => {
							e.currentTarget.style.color = '#475569';
							e.currentTarget.style.background = '#f1f5f9';
						}}
						onMouseOut={(e) => {
							e.currentTarget.style.color = '#475569';
							e.currentTarget.style.background = 'none';
						}}
					>
						<LuX size={14} />
					</button>
				</div>

				{/* Content */}
				<div
					className="overflow-y-auto p-6"
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 24,
						scrollbarWidth: 'thin',
						scrollbarColor: '#cbd5e1 transparent',
					}}
				>
					{/* Header Block */}
					<div>
						<label
							style={{
								display: 'block',
								color: '#334155',
								fontSize: 14,
								fontWeight: 600,
								marginBottom: 6,
							}}
						>
							{ml('Page Header', currentUser, allMultiLingualData)}{' '}
							<span style={{ color: '#475569', fontWeight: 400 }}>
								({ml('optional', currentUser, allMultiLingualData)})
							</span>
						</label>
						<p style={{ fontSize: 12, color: '#475569', margin: '0 0 10px' }}>
							{ml(
								'Add a title and description above the announcements feed.',
								currentUser,
								allMultiLingualData
							)}
						</p>
						<input
							type="text"
							value={headerTitle}
							onChange={(e) => setHeaderTitle(e.target.value)}
							placeholder={ml(
								'Header title...',
								currentUser,
								allMultiLingualData
							)}
							style={{
								width: '100%',
								padding: '10px 14px',
								borderRadius: 12,
								border: '1px solid #e2e8f0',
								background: '#f8fafc',
								outline: 'none',
								fontSize: 14,
								color: '#334155',
								marginBottom: 8,
								boxSizing: 'border-box',
								transition: 'all 0.2s',
							}}
							onFocus={(e) => {
								e.target.style.borderColor = lightenHex(accentColor);
								e.target.style.boxShadow = `0 0 0 3px ${accentRgba(accentColor, 0.1)}`;
							}}
							onBlur={(e) => {
								e.target.style.borderColor = '#e2e8f0';
								e.target.style.boxShadow = 'none';
							}}
						/>
						<input
							type="text"
							value={headerSubtext}
							onChange={(e) => setHeaderSubtext(e.target.value)}
							placeholder={ml(
								'Subtitle or description...',
								currentUser,
								allMultiLingualData
							)}
							style={{
								width: '100%',
								padding: '10px 14px',
								borderRadius: 12,
								border: '1px solid #e2e8f0',
								background: '#f8fafc',
								outline: 'none',
								fontSize: 13,
								color: '#334155',
								boxSizing: 'border-box',
								transition: 'all 0.2s',
							}}
							onFocus={(e) => {
								e.target.style.borderColor = lightenHex(accentColor);
								e.target.style.boxShadow = `0 0 0 3px ${accentRgba(accentColor, 0.1)}`;
							}}
							onBlur={(e) => {
								e.target.style.borderColor = '#e2e8f0';
								e.target.style.boxShadow = 'none';
							}}
						/>
					</div>

					{/* Accent Color */}
					<div>
						<label
							style={{
								display: 'block',
								color: '#334155',
								fontSize: 14,
								fontWeight: 600,
								marginBottom: 6,
							}}
						>
							{ml('Accent Color', currentUser, allMultiLingualData)}
						</label>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 10,
								marginBottom: 10,
							}}
						>
							<input
								type="color"
								value={accentColor}
								onChange={(e) => {
									setAccentColor(e.target.value);
									setAccentDraft(e.target.value);
								}}
								style={{
									width: 36,
									height: 36,
									borderRadius: 10,
									border: '2px solid #e2e8f0',
									padding: 2,
									cursor: 'pointer',
									background: '#fff',
								}}
							/>
							<input
								type="text"
								value={accentDraft}
								onChange={(e) => {
									setAccentDraft(e.target.value);
									if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) {
										setAccentColor(e.target.value);
									}
								}}
								style={{
									padding: '8px 12px',
									borderRadius: 10,
									border: '1px solid #e2e8f0',
									background: '#f8fafc',
									outline: 'none',
									fontSize: 13,
									color: '#334155',
									fontFamily: 'monospace',
									width: 100,
									transition: 'all 0.2s',
								}}
								onFocus={(e) => {
									e.target.style.borderColor = lightenHex(accentColor);
									e.target.style.boxShadow = `0 0 0 3px ${accentRgba(accentColor, 0.1)}`;
								}}
								onBlur={(e) => {
									e.target.style.borderColor = '#e2e8f0';
									e.target.style.boxShadow = 'none';
								}}
							/>
						</div>
						<div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
							{presetColors.map((color) => (
								<button
									key={color}
									type="button"
									onClick={() => {
										setAccentColor(color);
										setAccentDraft(color);
									}}
									style={{
										width: 28,
										height: 28,
										borderRadius: 8,
										background: color,
										border:
											accentColor === color
												? '2px solid #1e293b'
												: '2px solid transparent',
										cursor: 'pointer',
										transition: 'all 0.15s',
									}}
								/>
							))}
						</div>
					</div>

					{/* Pinned Icon Color */}
					<div>
						<label
							style={{
								display: 'block',
								color: '#334155',
								fontSize: 14,
								fontWeight: 600,
								marginBottom: 6,
							}}
						>
							{ml('Pinned Icon Color', currentUser, allMultiLingualData)}
						</label>
						<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
							<input
								type="color"
								value={pinnedColor}
								onChange={(e) => {
									setPinnedColor(e.target.value);
									setPinnedDraft(e.target.value);
								}}
								style={{
									width: 36,
									height: 36,
									borderRadius: 10,
									border: '2px solid #e2e8f0',
									padding: 2,
									cursor: 'pointer',
									background: '#fff',
								}}
							/>
							<input
								type="text"
								value={pinnedDraft}
								onChange={(e) => {
									setPinnedDraft(e.target.value);
									if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) {
										setPinnedColor(e.target.value);
									}
								}}
								style={{
									padding: '8px 12px',
									borderRadius: 10,
									border: '1px solid #e2e8f0',
									background: '#f8fafc',
									outline: 'none',
									fontSize: 13,
									color: '#334155',
									fontFamily: 'monospace',
									width: 100,
									transition: 'all 0.2s',
								}}
								onFocus={(e) => {
									e.target.style.borderColor = lightenHex(accentColor);
									e.target.style.boxShadow = `0 0 0 3px ${accentRgba(accentColor, 0.1)}`;
								}}
								onBlur={(e) => {
									e.target.style.borderColor = '#e2e8f0';
									e.target.style.boxShadow = 'none';
								}}
							/>
							{/* Preview */}
							<div
								style={{
									width: 28,
									height: 28,
									borderRadius: '50%',
									background: pinnedColor,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									color: '#fff',
									lineHeight: 0,
								}}
							>
								<LuPin size={12} />
							</div>
						</div>
					</div>

					{/* Gradient Bar */}
					<div>
						<label
							style={{
								display: 'block',
								color: '#334155',
								fontSize: 14,
								fontWeight: 600,
								marginBottom: 6,
							}}
						>
							{ml('Card Accent Gradient', currentUser, allMultiLingualData)}
						</label>
						<p style={{ fontSize: 12, color: '#475569', margin: '0 0 10px' }}>
							{ml(
								'The colored bar on cards without images.',
								currentUser,
								allMultiLingualData
							)}
						</p>
						{/* Preview */}
						<div
							style={{
								height: 12,
								borderRadius: 8,
								background: gradientPreview,
								marginBottom: 12,
							}}
						/>
						<div style={{ display: 'flex', gap: 8 }}>
							{[
								{
									label: 'Start',
									value: gradientStart,
									setter: setGradientStart,
								},
								{ label: 'Middle', value: gradientMid, setter: setGradientMid },
								{ label: 'End', value: gradientEnd, setter: setGradientEnd },
							].map(({ label, value, setter }) => (
								<div key={label} style={{ flex: 1, textAlign: 'center' }}>
									<p
										style={{
											fontSize: 11,
											color: '#475569',
											margin: '0 0 4px',
											fontWeight: 500,
										}}
									>
										{label}
									</p>
									<input
										type="color"
										value={value}
										onChange={(e) => setter(e.target.value)}
										style={{
											width: '100%',
											height: 32,
											borderRadius: 8,
											border: '2px solid #e2e8f0',
											padding: 2,
											cursor: 'pointer',
											background: '#fff',
										}}
									/>
								</div>
							))}
						</div>
					</div>

					{/* Display Options */}
					<div>
						<label
							style={{
								display: 'block',
								color: '#334155',
								fontSize: 14,
								fontWeight: 600,
								marginBottom: 10,
							}}
						>
							{ml('Display Options', currentUser, allMultiLingualData)}
						</label>
						<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
							<div style={{ display: 'flex', gap: 20 }}>
								<Checkbox
									checked={showAuthor}
									onChange={(e) => setShowAuthor(e.target.checked)}
								>
									<span className="text-sm" style={{ color: '#334155' }}>
										{ml('Show Author', currentUser, allMultiLingualData)}
									</span>
								</Checkbox>
								<Checkbox
									checked={showViews}
									onChange={(e) => setShowViews(e.target.checked)}
								>
									<span className="text-sm" style={{ color: '#334155' }}>
										{ml('Show View Count', currentUser, allMultiLingualData)}
									</span>
								</Checkbox>
								<Checkbox
									checked={showCount}
									onChange={(e) => setShowCount(e.target.checked)}
								>
									<span className="text-sm" style={{ color: '#334155' }}>
										{ml(
											'Show Announcement Count',
											currentUser,
											allMultiLingualData
										)}
									</span>
								</Checkbox>
							</div>
							<div style={{ marginTop: 4 }}>
								<label
									style={{
										display: 'block',
										color: '#334155',
										fontSize: 13,
										fontWeight: 500,
										marginBottom: 4,
									}}
								>
									{ml('Default Author Name', currentUser, allMultiLingualData)}
								</label>
								<input
									type="text"
									value={defaultAuthor}
									onChange={(e) => setDefaultAuthor(e.target.value)}
									placeholder={ml(
										'E.g. Company Name',
										currentUser,
										allMultiLingualData
									)}
									style={{
										width: '100%',
										padding: '8px 14px',
										borderRadius: 10,
										border: '1px solid #e2e8f0',
										background: '#f8fafc',
										outline: 'none',
										fontSize: 13,
										color: '#334155',
										boxSizing: 'border-box',
										transition: 'all 0.2s',
									}}
									onFocus={(e) => {
										e.target.style.borderColor = lightenHex(accentColor);
										e.target.style.boxShadow = `0 0 0 3px ${accentRgba(accentColor, 0.1)}`;
									}}
									onBlur={(e) => {
										e.target.style.borderColor = '#e2e8f0';
										e.target.style.boxShadow = 'none';
									}}
								/>
							</div>
						</div>
					</div>

					{/* Save & Close button */}
					<button
						type="button"
						onClick={() => {
							onSave({
								headerTitle,
								headerSubtext,
								accentColor,
								pinnedColor,
								gradientStart,
								gradientMid,
								gradientEnd,
								showAuthor,
								showViews,
								showCount,
								defaultAuthor,
							});
							onClose();
						}}
						style={{
							paddingTop: 10,
							paddingBottom: 10,
							borderRadius: '0.75rem',
							fontSize: 14,
							fontWeight: 600,
							color: '#fff',
							background: accentColor,
							border: 'none',
							cursor: 'pointer',
							transition: 'all 0.2s',
						}}
						onMouseOver={(e) => {
							e.currentTarget.style.background = darkenHex(accentColor);
						}}
						onMouseOut={(e) => {
							e.currentTarget.style.background = accentColor;
						}}
					>
						{ml('Save & Close', currentUser, allMultiLingualData)}
					</button>
				</div>
			</motion.div>
		</div>
	);
}

export default SettingsModal;
